import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCourseApi } from '../../hooks/useCourseApi';
import { useAuth } from '../../contexts/AuthContext';
import { walletService } from '../../services/walletService';
import { formatCoins } from '../../utils/walletLabels';
import { CreditCard, Wallet, ShoppingCart } from 'lucide-react';

interface EnrollmentPopupProps {
  isOpen: boolean;
  onClose: () => void;
  courseId: string;
  courseName?: string;
  coursePrice?: number;
}

const EnrollmentPopup: React.FC<EnrollmentPopupProps> = ({
  isOpen,
  onClose,
  courseId,
  courseName,
  coursePrice = 0,
}) => {
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const navigate = useNavigate();
  const { enrollInCourse } = useCourseApi();
  const { isAuthenticated } = useAuth();

  const isPaidCourse = coursePrice > 0;
  const coinsRequired = Math.round(coursePrice);
  const hasEnoughCoins = walletBalance !== null && walletBalance >= coinsRequired;

  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      setError('');
      setCode('');
      setSuccess(false);
      setShowCodeInput(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const loadBalance = async () => {
      if (!isOpen || !isAuthenticated || !isPaidCourse) return;
      const result = await walletService.getMyHistory(1, 1);
      if (result.success) {
        setWalletBalance(result.data.history.currentBalance);
      }
    };
    loadBalance();
  }, [isOpen, isAuthenticated, isPaidCourse]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setShowCodeInput(false);
      setError('');
      setCode('');
      setSuccess(false);
      setIsClosing(false);
    }, 200);
  };

  const handleCheckout = () => {
    if (!isAuthenticated) {
      setError('Please log in to enroll in this course');
      return;
    }
    handleClose();
    navigate('/checkout', {
      state: {
        courseId,
        course: {
          id: courseId,
          courseName: courseName ?? '',
          price: coursePrice,
        },
      },
    });
  };

  const handleWalletPurchase = async () => {
    if (!isAuthenticated) {
      setError('Please log in to enroll in this course');
      return;
    }

    if (walletBalance !== null && walletBalance < coinsRequired) {
      setError(`رصيدك غير كافٍ. المطلوب: ${formatCoins(coinsRequired)} جنيه`);
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      const result = await walletService.buyCourseWithCoins(courseId);
      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.error);
      }
    } catch {
      setError('حدث خطأ أثناء شراء الكورس من المحفظة');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeSubmit = async () => {
    if (!code.trim()) {
      setError('Please enter an enrollment code');
      setSuccess(false);
      return;
    }

    if (!isAuthenticated) {
      setError('Please log in to enroll in this course');
      setSuccess(false);
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      setSuccess(false);
      const response = await enrollInCourse(courseId, code);

      if (response.success && response.message !== 'Code not found.') {
        setSuccess(true);
        setError('');
      } else {
        setSuccess(false);
        setError(response.message || 'Invalid enrollment code');
      }
    } catch {
      setSuccess(false);
      setError('An error occurred while enrolling in the course');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isLoading) {
      handleCodeSubmit();
    }
  };

  const handleGoToCourse = () => {
    navigate(`/course-player/${courseId}`);
    onClose();
  };

  const translateError = (msg: string) => {
    if (msg === 'Please enter an enrollment code') return 'يرجى إدخال كود التسجيل';
    if (msg === 'Please log in to enroll in this course') return 'يرجى تسجيل الدخول للتسجيل في هذه الدورة';
    if (msg === 'Invalid enrollment code') return 'كود التسجيل غير صالح';
    if (msg === 'Code not found.') return 'الكود غير موجود';
    if (msg === 'An error occurred while enrolling in the course') return 'حدث خطأ أثناء التسجيل في الدورة';
    return msg;
  };

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${isClosing ? 'animate-fadeOut' : 'animate-fadeIn'}`}>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />
      <div
        className={`relative w-full max-w-lg transform rounded-2xl bg-white shadow-2xl transition-all duration-300
          ${isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}
          ${showCodeInput ? 'sm:max-w-md' : 'sm:max-w-lg'}`}
      >
        <div className="p-6 sm:p-8" dir="rtl">
          {!showCodeInput && !success && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">اختر طريقة التسجيل</h2>
                <p className="text-gray-600">حدد كيف تريد التسجيل في هذه الدورة</p>
              </div>

              {error && (
                <div className="text-red-500 text-sm flex items-center gap-1 bg-red-50 rounded-lg py-2 px-3">
                  {translateError(error)}
                </div>
              )}

              <div className="space-y-3">
                {isPaidCourse && (
                  <>
                    <button
                      onClick={() => {
                        setError('');
                        handleCheckout();
                      }}
                      className="group relative w-full flex items-center justify-center gap-3 py-3 px-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-green-500/25"
                    >
                      <CreditCard className="w-5 h-5" />
                      الدفع عبر بوابة الدفع
                      <span className="absolute left-4 opacity-0 group-hover:opacity-100 transition-opacity">←</span>
                    </button>

                    <button
                      onClick={() => {
                        setError('');
                        handleWalletPurchase();
                      }}
                      disabled={isLoading}
                      className="group relative w-full flex items-center justify-center gap-3 py-3 px-4 bg-gradient-to-r from-gold-500 to-gold-600 text-white rounded-xl hover:from-gold-600 hover:to-gold-700 transition-all duration-200 shadow-lg hover:shadow-gold-500/25 disabled:opacity-50"
                    >
                      <Wallet className="w-5 h-5" />
                      {isLoading
                        ? 'جاري الشراء...'
                        : `الدفع من المحفظة (${formatCoins(coinsRequired)} جنيه)`}
                      <span className="absolute left-4 opacity-0 group-hover:opacity-100 transition-opacity">←</span>
                    </button>

                    {walletBalance !== null && !hasEnoughCoins && (
                      <p className="text-xs text-center text-amber-700 bg-amber-50 rounded-lg py-2 px-3">
                        رصيدك: {formatCoins(walletBalance)} جنيه ·{' '}
                        <Link to="/student-portal/wallet" className="underline font-medium" onClick={handleClose}>
                          شحن المحفظة
                        </Link>
                      </p>
                    )}
                  </>
                )}

                <button
                  onClick={() => {
                    setError('');
                    setShowCodeInput(true);
                  }}
                  className="group relative w-full flex items-center justify-center gap-3 py-3 px-4 bg-primary text-white rounded-xl hover:bg-primary/90 transition-all duration-200 shadow-lg hover:shadow-primary/25"
                >
                  <ShoppingCart className="w-5 h-5" />
                  لدي كود تسجيل
                  <span className="absolute left-4 opacity-0 group-hover:opacity-100 transition-opacity">←</span>
                </button>
              </div>
              <button
                onClick={handleClose}
                className="w-full py-2 px-4 text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                إلغاء
              </button>
            </div>
          )}

          {showCodeInput && !success && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">أدخل الكود</h2>
                <p className="text-gray-600">يرجى إدخال كود التسجيل أدناه</p>
              </div>
              <div className="space-y-4">
                <div className="relative">
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="أدخل كود التسجيل"
                    className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-colors duration-200
                      ${error ? 'border-red-300 bg-red-50' : 'border-gray-200 focus:border-primary'}
                    `}
                    disabled={isLoading}
                  />
                  {error && (
                    <div className="mt-2 text-red-500 text-sm flex items-center gap-1">
                      {translateError(error)}
                    </div>
                  )}
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={handleCodeSubmit}
                    disabled={isLoading}
                    className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all duration-200
                      ${isLoading
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-primary text-white hover:bg-primary/90 shadow-lg hover:shadow-primary/25'
                      }
                    `}
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        جاري التحقق...
                      </span>
                    ) : 'تحقق من الكود'}
                  </button>
                  <button
                    onClick={() => {
                      setShowCodeInput(false);
                      setError('');
                      setCode('');
                    }}
                    className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200"
                    disabled={isLoading}
                  >
                    رجوع
                  </button>
                </div>
              </div>
            </div>
          )}

          {success && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">تم التسجيل بنجاح!</h2>
                <p className="text-gray-600">يمكنك الآن بدء التعلم</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleGoToCourse}
                  className="flex-1 py-3 px-4 bg-primary text-white rounded-xl hover:bg-primary-dark transition-all duration-200"
                >
                  بدء الدورة
                </button>
                <button
                  onClick={() => navigate('/student-portal/my-courses')}
                  className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200"
                >
                  عرض باقاتي
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnrollmentPopup;
